import type { HelpDocId } from './content';

export interface HelpSectionRef {
  doc: HelpDocId;
  /** github-slugger slug of the target heading — verified against the real
   * doc's headings by helpMap.test.ts (the anchor-integrity test) so a future
   * doc edit that renames/removes a heading fails CI instead of silently
   * breaking contextual help. */
  slug: string;
}

export interface HelpMapEntry {
  /** Route pathname prefix this entry covers. Longest match wins (see
   * `findHelpMapEntry`) — e.g. '/settings/xero' beats '/settings' for
   * '/settings/xero/callback'. */
  match: string;
  title: string;
  sections: HelpSectionRef[];
}

/** Home ('/') is also the fallback entry: `findHelpMapEntry` returns it for
 * any authenticated route with no dedicated mapping (e.g. '/help' itself —
 * harmless, since /help doesn't render the contextual help button anyway). */
const HOME_ENTRY: HelpMapEntry = {
  match: '/',
  title: 'Home',
  sections: [
    { doc: 'user', slug: '2-core-concepts-10-minutes' },
    { doc: 'user', slug: '3-a-day-in-the-life' },
  ],
};

/** Route → help-section mapping. Built by reading the actual headings in
 * docs/USER_GUIDE.md, docs/OPERATIONS_PLAYBOOK.md and docs/ADMIN_GUIDE.md and
 * slugging them with github-slugger (not guessed) — see helpMap.test.ts. */
export const HELP_MAP: HelpMapEntry[] = [
  HOME_ENTRY,
  // Products module (2026-08-26 — Stuart's front-and-centre ruling, nav.ts).
  // Both anchors are Section 2 "Core concepts" headings: the list screen's
  // qty/avg-cost/value columns are exactly the products/pools/WAC concepts
  // those headings introduce, so they're a better contextual match than any
  // single §4 workflow chapter (this screen isn't one step of a workflow).
  {
    match: '/products',
    title: 'Products',
    sections: [
      { doc: 'user', slug: 'products--stock-pools' },
      { doc: 'user', slug: 'weighted-average-cost-wac' },
      // WALKTHROUGH.md's dedicated Products deep-dive (list columns, the
      // headline numbers rail, Inventory/Transactions/Costs tabs) plus the
      // guided-week step that confirms a receipt landed on the product page —
      // both a closer topical match for this exact screen than the Core
      // Concepts headings above, added alongside them rather than replacing
      // them (2026-08-27 walkthrough rollout).
      { doc: 'walkthrough', slug: 'reading-the-numbers' },
      { doc: 'walkthrough', slug: 'checking-the-product-page-after-a-receipt' },
    ],
  },
  {
    match: '/purchasing-intake',
    title: 'Purchasing & Intake',
    sections: [
      { doc: 'user', slug: '4a-purchasing' },
      { doc: 'user', slug: '4b-receiving-a-load' },
      // The no-ticket branch (Stuart ruling 2026-08-25: folded into "Receive
      // a load" as an optional step, no longer its own screen/route) — kept
      // here so its dedicated docket-entry walkthrough is still reachable.
      { doc: 'user', slug: '6d-receiving-without-the-weighbridge' },
      { doc: 'playbook', slug: '6-weighbridge-integrity' },
      { doc: 'playbook', slug: '3-moisture-discipline' },
    ],
  },
  // Longer prefix than '/purchasing-intake', so it wins for the supplier
  // contracts screen (longest-prefix lookup): this screen's dedicated
  // USER_GUIDE walkthrough (moisture-band pricing, entering non-overlapping
  // contiguous bands) would otherwise be buried under the parent's PO/receiving
  // sections, which don't cover it at all (doc-completeness audit fix
  // 2026-07-10).
  {
    match: '/purchasing-intake/contracts',
    title: 'Supplier contracts',
    sections: [{ doc: 'user', slug: 'supplier-contracts' }],
  },
  {
    match: '/production-packconv',
    title: 'Production & Packing',
    sections: [
      { doc: 'user', slug: '4c-production-runs' },
      { doc: 'user', slug: '4h-pack-conversions' },
      { doc: 'playbook', slug: '2-the-recipe-accuracy-loop' },
    ],
  },
  // Longer prefix than '/production-packconv', so it wins for the Quick
  // conversion screen (longest-prefix lookup) — same precedent as
  // '/purchasing-intake/contracts' above: this screen isn't documented at all
  // in USER_GUIDE.md (it's a Howick-only, walkthrough-first feature), so the
  // parent's full-shift-oriented sections above would otherwise be the only
  // help offered here (2026-08-27 walkthrough rollout).
  {
    match: '/production-packconv/quick',
    title: 'Quick conversion',
    sections: [{ doc: 'walkthrough', slug: 'quick-conversion-kiln-drying' }],
  },
  // Split path-wise (Stuart ruling 2026-08-26: silos are a per-tenant module,
  // default OFF — Howick has none, so its "Silo & Counts" section is just
  // "Stock counts", and its contextual help should only ever offer the counts
  // doc, never the silo-reading/reconciliation content it can't act on). The
  // three silo-only screens get their own longer-prefix entries (longest-
  // prefix-wins lookup, same precedent as '/purchasing-intake/contracts'
  // above); the generalized '/silo-counts' entry below now covers Stock
  // counts and anything else under the section root.
  {
    match: '/silo-counts/status',
    title: 'Silo status',
    sections: [
      { doc: 'user', slug: '4d-silo-levels--reconciliation' },
      { doc: 'playbook', slug: '4-silo-reconciliation-rhythm' },
    ],
  },
  {
    match: '/silo-counts/calibration',
    title: 'Silo calibration',
    sections: [
      { doc: 'user', slug: '4d-silo-levels--reconciliation' },
      { doc: 'playbook', slug: '4-silo-reconciliation-rhythm' },
    ],
  },
  {
    match: '/silo-counts/new-reading',
    title: 'New silo reading',
    sections: [
      { doc: 'user', slug: '4d-silo-levels--reconciliation' },
      { doc: 'playbook', slug: '4-silo-reconciliation-rhythm' },
    ],
  },
  {
    match: '/silo-counts',
    title: 'Stock counts',
    sections: [{ doc: 'user', slug: '4i-stocktakes--counts' }],
  },
  {
    match: '/sales-invoicing',
    title: 'Sales & Invoicing',
    sections: [
      { doc: 'user', slug: '4f-sales-orders--dispatch' },
      { doc: 'user', slug: '4g-invoicing--credit-notes' },
      { doc: 'playbook', slug: '6-weighbridge-integrity' },
    ],
  },
  // Longer prefix than '/sales-invoicing', so it wins for the sales-orders
  // register/create/detail screens (longest-prefix lookup) — same precedent as
  // '/purchasing-intake/contracts' above: a screen whose own dedicated
  // USER_GUIDE subsection would otherwise be buried under the parent's more
  // general dispatch-centric sections. Both sections are deliberate: the
  // dedicated sales-order walkthrough first, then §4f for the dispatch-side
  // context. They render without overlap ONLY because both headings are
  // sibling h3s in USER_GUIDE.md — extractParsedSection cuts a section at the
  // next same-or-shallower heading, so §4f's extract stops right before
  // "Managing a sales order". (Wave-gate finding 2026-07-10: while that
  // heading was an h4 NESTED inside §4f, the drawer rendered it twice —
  // HelpDrawer.test.tsx now pins the exactly-once behaviour.)
  {
    match: '/sales-invoicing/sales-orders',
    title: 'Sales orders',
    sections: [
      { doc: 'user', slug: 'managing-a-sales-order' },
      { doc: 'user', slug: '4f-sales-orders--dispatch' },
    ],
  },
  // Longer prefix than '/sales-invoicing', so it wins for the ticket-less
  // dispatch screen (longest-prefix lookup) — same precedent as
  // '/purchasing-intake/contracts' above: this screen's help was
  // otherwise falling back to the weighbridge-centric §4f steps, which
  // actively contradict it. USER_GUIDE's Howick chapter has the dedicated
  // twin content: 6g (the ticket-less dispatch flow itself).
  {
    match: '/sales-invoicing/dispatch-manual',
    title: 'Dispatch without weighbridge',
    sections: [{ doc: 'user', slug: '6g-dispatching-without-the-weighbridge' }],
  },
  {
    match: '/stores-transfers',
    title: 'Stores & Transfers',
    sections: [
      { doc: 'user', slug: '4e-stores--consumables' },
      { doc: 'playbook', slug: '5-stores--consumables-stocktake' },
    ],
  },
  // Longer prefix than '/stores-transfers', so it wins for the transfer
  // create/detail screens (longest-prefix lookup) — same precedent as
  // '/sales-invoicing/sales-orders' above: the dedicated USER_GUIDE Transfers
  // walkthrough (route, cross-site vs same-site, the conservation guard) would
  // otherwise be buried under the parent's Issues-centric section, which never
  // mentions transfers at all (doc-completeness audit fix 2026-07-10).
  {
    match: '/stores-transfers/transfers',
    title: 'Transfers',
    sections: [{ doc: 'user', slug: 'transfers' }],
  },
  {
    match: '/reports',
    title: 'Reports',
    sections: [
      { doc: 'user', slug: '4l-reports' },
      { doc: 'playbook', slug: '9-the-operating-rhythm-on-one-page' },
    ],
  },
  {
    match: '/search',
    title: 'Search',
    sections: [{ doc: 'user', slug: '4m-search' }],
  },
  // Longer, more specific /settings/* prefixes are listed before the plain
  // '/settings' entry for readability, but ordering has no effect on lookup —
  // findHelpMapEntry always picks the longest matching `match` string.
  {
    match: '/settings/xero',
    title: 'Settings — Xero',
    sections: [{ doc: 'admin', slug: '6-xero-connection' }],
  },
  {
    match: '/settings/importer',
    title: 'Settings — Importer',
    sections: [{ doc: 'admin', slug: '5-unleashed-import-howick-onboarding' }],
  },
  {
    match: '/settings',
    title: 'Settings',
    sections: [
      { doc: 'admin', slug: '3-initial-tenant-setup' },
      { doc: 'admin', slug: '4-users-roles--permissions' },
    ],
  },
];

function isPrefixMatch(pathname: string, prefix: string): boolean {
  if (prefix === '/') return true;
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Longest-prefix-wins lookup used by HelpDrawer.tsx/HelpButton.tsx: finds
 * every HELP_MAP entry whose `match` is a prefix of `pathname`, then returns
 * the one with the longest `match`. Trailing slashes are normalised away
 * first ('/settings/' and '/settings' are the same screen). '/' always
 * matches, so this never returns undefined — any unmapped route falls back
 * to the Home entry. */
export function findHelpMapEntry(pathname: string): HelpMapEntry {
  const normalised = pathname.length > 1 ? pathname.replace(/\/+$/, '') || '/' : pathname;
  const matches = HELP_MAP.filter((entry) => isPrefixMatch(normalised, entry.match));
  if (matches.length === 0) return HOME_ENTRY;
  return matches.reduce((longest, entry) => (entry.match.length > longest.match.length ? entry : longest));
}
